import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, BooleanDecoder, EnumDecoder, IntegerDecoder, MapDecoder, StringDecoder, field } from '@simonbackx/simple-encoding';
import { v4 as uuidv4 } from 'uuid';
import { Formatter } from '@stamhoofd/utility';
import { upgradePriceFrom2To4DecimalPlaces } from '../upgradePriceFrom2To4DecimalPlaces.js';
export var OptionSelectionRequirement;
(function (OptionSelectionRequirement) {
    OptionSelectionRequirement["Required"] = "Required";
    OptionSelectionRequirement["Optional"] = "Optional";
    OptionSelectionRequirement["Excluded"] = "Excluded";
})(OptionSelectionRequirement || (OptionSelectionRequirement = {}));
export class OptionSelectionRequirementHelper {
    static getName(requirement) {
        switch (requirement) {
            case OptionSelectionRequirement.Required: return $t(`%sp`);
            case OptionSelectionRequirement.Optional: return $t(`%sq`);
            case OptionSelectionRequirement.Excluded: return $t(`%sr`);
        }
    }
}
export class ProductSelector extends AutoEncoder {
    productId;
    /**
     * Empty array = doesn't matter
     * Array set = required one of the product prices from the list
     */
    productPriceIds = [];
    /**
     * Options that are missing in the list get a default value
     * depending whether the option menu is multiple choice or not.
     *
     * For multiple choice:
     * - new options are optional
     *
     * For choose one:
     * - new options are exluded unless all options are optional
     */
    optionIds = new Map();
    getOptionRequirement(optionMenu, option) {
        const value = this.optionIds.get(option.id);
        if (!value) {
            if (optionMenu.multipleChoice) {
                return OptionSelectionRequirement.Optional;
            }
            else {
                for (const o of optionMenu.options) {
                    if (this.optionIds.get(o.id) ?? OptionSelectionRequirement.Optional !== OptionSelectionRequirement.Optional) {
                        return OptionSelectionRequirement.Excluded;
                    }
                }
                return OptionSelectionRequirement.Optional;
            }
        }
        return value;
    }
    doesMatch(cartItem) {
        if (cartItem.product.id !== this.productId) {
            return false;
        }
        if (this.productPriceIds.includes(cartItem.productPrice.id)) {
            return false;
        }
        for (const option of cartItem.options) {
            const value = this.getOptionRequirement(option.optionMenu, option.option);
            if (value === OptionSelectionRequirement.Excluded) {
                return false;
            }
        }
        for (const [id, requirement] of this.optionIds) {
            if (requirement === OptionSelectionRequirement.Required) {
                const found = cartItem.options.find(o => o.option.id === id);
                if (!found) {
                    return false;
                }
            }
        }
        return true;
    }
    getName(webshop, isAdmin = false) {
        const product = webshop.products.find(p => p.id === this.productId);
        if (!product) {
            return {
                name: $t(`%Sb`),
                footnote: '',
            };
        }
        const allPrices = product.filteredPrices({ admin: isAdmin });
        const productPrices = allPrices.filter(p => this.productPriceIds.includes(p.id));
        let suffix = '';
        if (allPrices.length > 1 && this.productPriceIds.length && productPrices.length < allPrices.length) {
            suffix = productPrices.map(p => p.name).join(', ');
        }
        let footnote = '';
        const excludedOptions = product.optionMenus.flatMap((menu) => {
            return menu.options.filter(o => this.getOptionRequirement(menu, o) === OptionSelectionRequirement.Excluded);
        });
        const requiredOptions = product.optionMenus.flatMap((menu) => {
            return menu.options.filter(o => this.getOptionRequirement(menu, o) === OptionSelectionRequirement.Required);
        });
        if (excludedOptions.length && requiredOptions.length === 0) {
            footnote = $t(`%ss`) + ' ' + Formatter.joinLast(excludedOptions.map(o => o.name), ', ', ' ' + $t(`%GT`) + ' ');
        }
        else if (excludedOptions.length === 0 && requiredOptions.length) {
            footnote = $t(`%st`) + ' ' + Formatter.joinLast(requiredOptions.map(o => o.name), ', ', ' ' + $t(`%M1`) + ' ');
        }
        else if (excludedOptions.length && requiredOptions.length) {
            footnote = $t(`%st`) + ' ' + Formatter.joinLast(requiredOptions.map(o => o.name), ', ', ' ' + $t(`%M1`) + ' ') + ' ' + $t(`%su`) + ' ' + Formatter.joinLast(excludedOptions.map(o => o.name), ', ', ' ' + $t(`%GT`) + ' ');
        }
        return {
            name: product.name + (suffix ? (' (' + suffix + ')') : ''),
            footnote,
        };
    }
}
__decorate([
    field({ decoder: StringDecoder })
], ProductSelector.prototype, "productId", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(StringDecoder) })
], ProductSelector.prototype, "productPriceIds", void 0);
__decorate([
    field({ decoder: new MapDecoder(StringDecoder, new EnumDecoder(OptionSelectionRequirement)) })
], ProductSelector.prototype, "optionIds", void 0);
export class ProductsSelector extends AutoEncoder {
    products = [];
    doesMatch(cartItem) {
        for (const product of this.products) {
            if (product.doesMatch(cartItem)) {
                return true;
            }
        }
        return false;
    }
    getName(webshop, isAdmin = false) {
        const names = [];
        const footnotes = [];
        for (const product of this.products) {
            const n = product.getName(webshop, isAdmin);
            names.push(n.name);
            if (n.footnote) {
                footnotes.push(n.footnote);
            }
        }
        return {
            name: Formatter.joinLast(names, ', ', ' of '),
            footnote: footnotes.join('\n'),
        };
    }
    static decode(data) {
        if (data.optionalField('productId') && !data.optionalField('products')) {
            // Soft version upgrade: decode as ProductSelector
            const ps = ProductSelector.decode(data);
            const pss = ProductsSelector.create({
                products: [ps],
            });
            return pss;
        }
        const decoded = super.decode(data);
        return decoded;
    }
    encode(context) {
        if (this.products.length === 1) {
            // Soft version upgrade: encode as ProductSelector
            return this.products[0].encode(context);
        }
        return super.encode(context);
    }
}
__decorate([
    field({ decoder: new ArrayDecoder(ProductSelector) })
], ProductsSelector.prototype, "products", void 0);
export class DiscountRequirement extends AutoEncoder {
    id;
    product;
    /**
     * Minimum amount to trigger this requirement
     */
    amount = 1;
    /**
     * Requirements can match multiple times (for X + Y discount systems)
     */
    getMatchCount(checkout) {
        if (this.amount === 0) {
            return 0;
        }
        let amount = 0;
        for (const item of checkout.cart.items) {
            if (this.product.doesMatch(item)) {
                amount += item.amount;
            }
        }
        return Math.floor(amount / this.amount);
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], DiscountRequirement.prototype, "id", void 0);
__decorate([
    field({ decoder: ProductsSelector })
], DiscountRequirement.prototype, "product", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], DiscountRequirement.prototype, "amount", void 0);
export class GeneralDiscount extends AutoEncoder {
    /**
     * Fixed discount on order
     */
    fixedDiscount = 0;
    /**
     * Percentage discount on order, in pertenthousand 1 / 10 000
     * 10 = 0,1% discount
     * 1 = 0,01% discount
     */
    percentageDiscount = 0;
    multiply(amount) {
        return GeneralDiscount.create({
            fixedDiscount: Math.round(this.fixedDiscount * amount),
            percentageDiscount: Math.round(this.percentageDiscount * amount),
        });
    }
}
__decorate([
    field({ decoder: IntegerDecoder }),
    field({ ...upgradePriceFrom2To4DecimalPlaces })
], GeneralDiscount.prototype, "fixedDiscount", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], GeneralDiscount.prototype, "percentageDiscount", void 0);
export class ProductDiscount extends AutoEncoder {
    /**
     * Id determines uniqueness so should not be reused between discounts
     */
    id;
    discountPerPiece = 0;
    /**
     * Percentage discount on order, in pertenthousand 1 / 10 000
     * 10 = 0,1% discount
     * 1 = 0,01% discount
     */
    percentageDiscount = 0;
    calculate(price) {
        price = Math.min(price, Math.max(0, 100 * Math.round(price * (10000 - this.percentageDiscount) / 10000_00))); // Min is required to support negative prices: prices should never increase after applyign discounts
        price = Math.min(price, Math.max(0, price - this.discountPerPiece)); // Min is required to support negative prices: prices should never increase after applyign discounts
        return price;
    }
    applyTo(item) {
        // Percentage discounts are always applied to the original price without other discounts
        item.percentageDiscount = Math.min(10000, item.percentageDiscount + this.percentageDiscount);
        item.fixedDiscount = item.fixedDiscount + this.discountPerPiece;
    }
    calculatePotential(item) {
        const current = item;
        const potential = item.clone();
        this.applyTo(potential);
        return current.discountedPrice - potential.discountedPrice;
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], ProductDiscount.prototype, "id", void 0);
__decorate([
    field({ decoder: IntegerDecoder }),
    field({ ...upgradePriceFrom2To4DecimalPlaces })
], ProductDiscount.prototype, "discountPerPiece", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], ProductDiscount.prototype, "percentageDiscount", void 0);
export var ProductDiscountRepeatBehaviour;
(function (ProductDiscountRepeatBehaviour) {
    ProductDiscountRepeatBehaviour["Once"] = "Once";
    ProductDiscountRepeatBehaviour["RepeatLast"] = "RepeatLast";
    ProductDiscountRepeatBehaviour["RepeatPattern"] = "RepeatPattern";
})(ProductDiscountRepeatBehaviour || (ProductDiscountRepeatBehaviour = {}));
export class ProductDiscountSettings extends AutoEncoder {
    id;
    product;
    discounts = [ProductDiscount.create({})];
    repeatBehaviour = ProductDiscountRepeatBehaviour.Once;
    cartLabel = null;
    allowMultipleDiscountsToSameItem = true;
    getApplicableDiscounts(offset, amount) {
        const d = this.discounts.slice();
        if (this.repeatBehaviour === ProductDiscountRepeatBehaviour.RepeatLast) {
            while (d.length < offset + amount) {
                d.push(this.discounts[this.discounts.length - 1]);
            }
        }
        else if (this.repeatBehaviour === ProductDiscountRepeatBehaviour.RepeatPattern) {
            while (d.length < offset + amount) {
                d.push(this.discounts[d.length % this.discounts.length]);
            }
        }
        return d.slice(offset, offset + amount);
    }
    getTitle(webshop, isAdmin = false) {
        const n = this.product.getName(webshop, isAdmin);
        const titles = [n.name];
        let descriptions = [];
        const footnotes = [];
        if (n.footnote) {
            const index = '*'.repeat(footnotes.length + 1);
            titles.push(index);
            footnotes.push(index + ' ' + n.footnote);
        }
        if (this.cartLabel) {
            descriptions.push(this.cartLabel);
        }
        else if (this.discounts.length === 1) {
            const discount = this.discounts[0];
            if (discount.percentageDiscount) {
                if (discount.percentageDiscount >= 100 * 100) {
                    if (this.repeatBehaviour !== ProductDiscountRepeatBehaviour.Once) {
                        descriptions.push($t(`%1Mn`));
                    }
                    else {
                        descriptions.push($t(`%sv`));
                    }
                }
                else {
                    if (this.repeatBehaviour !== ProductDiscountRepeatBehaviour.Once) {
                        descriptions.push(Formatter.percentage(discount.percentageDiscount) + ' ' + $t(`%sT`));
                    }
                    else {
                        descriptions.push($t(`%sw`) + ' ' + Formatter.percentage(discount.percentageDiscount) + ' ' + $t(`%sT`));
                    }
                }
            }
            if (discount.discountPerPiece) {
                if (this.repeatBehaviour !== ProductDiscountRepeatBehaviour.Once) {
                    descriptions.push(Formatter.price(discount.discountPerPiece) + ' ' + $t(`%sx`));
                }
                else {
                    descriptions.push($t(`%sw`) + ' ' + Formatter.price(discount.discountPerPiece) + ' ' + $t(`%sT`));
                }
            }
        }
        else {
            let index = 0;
            for (const discount of this.repeatBehaviour === ProductDiscountRepeatBehaviour.RepeatPattern ? [...this.discounts, ...this.discounts, ...this.discounts] : this.discounts) {
                index += 1;
                let s = Formatter.ordinalNumber(index) + ' ' + $t(`%12Q`);
                if (index === this.discounts.length) {
                    if (this.repeatBehaviour === ProductDiscountRepeatBehaviour.RepeatLast) {
                        if (descriptions.length > 0) {
                            s = $t(`%sy`);
                        }
                        else {
                            s = $t(`%sz`) + ' ' + s;
                        }
                    }
                }
                if (discount.percentageDiscount) {
                    if (discount.percentageDiscount >= 100 * 100) {
                        descriptions.push(s + ' ' + $t(`%s8`));
                    }
                    else {
                        descriptions.push(Formatter.percentage(discount.percentageDiscount) + ' ' + $t(`%t0`) + ' ' + s);
                    }
                }
                if (discount.discountPerPiece) {
                    descriptions.push(Formatter.price(discount.discountPerPiece) + ' ' + $t(`%t0`) + ' ' + s);
                }
            }
            if (this.repeatBehaviour === ProductDiscountRepeatBehaviour.RepeatPattern) {
                if (descriptions.length === 3 && this.discounts[this.discounts.length - 1].percentageDiscount === 100 * 100) {
                    descriptions = [(this.discounts.length - 1) + ' ' + $t(`%t1`)];
                }
                else {
                    descriptions.push('...');
                }
            }
        }
        if (descriptions.length === 0) {
            descriptions.push($t(`%t2`));
        }
        return {
            title: titles.join(' '),
            description: Formatter.capitalizeFirstLetter(Formatter.joinLast(descriptions, ', ', ' ' + $t(`%M1`) + ' ')),
            footnote: footnotes.join('\n'),
        };
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], ProductDiscountSettings.prototype, "id", void 0);
__decorate([
    field({ decoder: ProductsSelector })
], ProductDiscountSettings.prototype, "product", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(ProductDiscount) })
], ProductDiscountSettings.prototype, "discounts", void 0);
__decorate([
    field({ decoder: new EnumDecoder(ProductDiscountRepeatBehaviour) })
], ProductDiscountSettings.prototype, "repeatBehaviour", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 238 })
], ProductDiscountSettings.prototype, "cartLabel", void 0);
__decorate([
    field({ decoder: BooleanDecoder, optional: true })
], ProductDiscountSettings.prototype, "allowMultipleDiscountsToSameItem", void 0);
export class ProductDiscountTracker {
    discount;
    usageCount = 0;
    cartItemQueue = [];
    constructor(discount) {
        this.discount = discount;
    }
    getNextDiscount(offset = 0) {
        const d = this.discount.getApplicableDiscounts(this.usageCount + offset, 1);
        if (d.length === 1) {
            return d[0];
        }
        return null;
    }
    sortQueue() {
        // Sort queue from highest price to lowest price
        this.cartItemQueue.sort((a, b) => {
            return b.price.discountedPrice - a.price.discountedPrice;
        });
    }
    get filteredQueue() {
        if (this.discount.allowMultipleDiscountsToSameItem) {
            return this.cartItemQueue;
        }
        return this.cartItemQueue.filter(i => i.price.fixedDiscount === 0 && i.price.percentageDiscount === 0);
    }
    apply() {
        // Make sure we use the next one in the discount applicable discounts
        this.sortQueue();
        this.cartItemQueue = this.filteredQueue;
        const item = this.cartItemQueue.shift();
        if (item) {
            const discount = this.getNextDiscount();
            if (discount) {
                const potential = discount.calculatePotential(item.price);
                this.usageCount += 1;
                discount.applyTo(item.price);
                if (potential > 0 && !item.item.discounts.find(d => d.id === this.discount.id)) {
                    item.item.discounts.push(this.discount);
                }
            }
        }
    }
    addItemToQueue(cartItem) {
        this.cartItemQueue.push(...cartItem.calculatedPrices.map((price) => {
            return { price, item: cartItem };
        }));
    }
    getPotentialDiscount() {
        let offset = 0;
        let potential = 0;
        this.sortQueue();
        for (const item of this.filteredQueue) {
            const d = this.getNextDiscount(offset);
            if (d) {
                potential += d.calculatePotential(item.price);
            }
            else {
                break;
            }
            offset += 1;
        }
        return potential;
    }
}
export class Discount extends AutoEncoder {
    id;
    /**
     * Empty list = no requirements
     */
    requirements = [];
    /**
     * If the requirements match x times, apply the discount x times
     */
    applyMultipleTimes = false;
    orderDiscount = GeneralDiscount.create({});
    productDiscounts = [];
    getTitle(webshop, isAdmin = false) {
        const titles = [];
        const footnotes = [];
        const descriptions = [];
        if (this.orderDiscount.percentageDiscount) {
            titles.push(Formatter.percentage(this.orderDiscount.percentageDiscount) + ' ' + $t(`%sT`));
        }
        if (this.orderDiscount.fixedDiscount) {
            titles.push(Formatter.price(this.orderDiscount.fixedDiscount) + ' ' + $t(`%sT`));
        }
        for (const productDiscount of this.productDiscounts) {
            const t = productDiscount.getTitle(webshop, isAdmin);
            titles.push(t.description + ' ' + $t(`%t3`) + ' ' + t.title);
            if (t.footnote) {
                footnotes.push(t.footnote);
            }
        }
        if (titles.length === 0) {
            return {
                title: $t(`%t2`),
                description: '',
                footnote: '',
            };
        }
        if (this.requirements.length) {
            if (this.applyMultipleTimes) {
                if (this.requirements.length > 1) {
                    descriptions.push($t(`%t4`));
                }
                else {
                    descriptions.push($t(`%t5`));
                }
            }
            else {
                descriptions.push($t(`%t6`));
            }
            const subdescriptions = [];
            for (const requirement of this.requirements) {
                const n = requirement.product.getName(webshop, isAdmin);
                subdescriptions.push(requirement.amount + ' x ' + n.name);
                if (n.footnote) {
                    const index = '*'.repeat(footnotes.length + 1);
                    subdescriptions[subdescriptions.length - 1] = subdescriptions[subdescriptions.length - 1] + index;
                    footnotes.push(index + ' ' + n.footnote);
                }
            }
            descriptions.push(Formatter.joinLast(subdescriptions, ', ', ' ' + $t(`%M1`) + ' '));
        }
        return {
            title: titles.join(' '),
            description: descriptions.join(' '),
            footnote: footnotes.join('\n'),
        };
    }
    applyToCheckout(checkout) {
        let matchCount = null;
        if (this.requirements.length > 0) {
            for (const requirement of this.requirements) {
                const amount = requirement.getMatchCount(checkout);
                if (amount === 0) {
                    // Not applicable
                    return [];
                }
                matchCount = matchCount === null ? amount : Math.min(amount, matchCount);
            }
        }
        if (matchCount === null) {
            matchCount = 1;
        }
        if (!this.applyMultipleTimes) {
            matchCount = 1;
        }
        // Fixed part to the whole order
        const multipliedOrderDiscount = this.orderDiscount.multiply(matchCount ?? 1);
        checkout.fixedDiscount += multipliedOrderDiscount.fixedDiscount;
        checkout.percentageDiscount = Math.min(10000, checkout.percentageDiscount + multipliedOrderDiscount.percentageDiscount);
        const trackers = [];
        // Per product
        for (const discount of this.productDiscounts) {
            for (let index = 0; index < matchCount; index++) {
                const tracker = new ProductDiscountTracker(discount);
                for (const item of checkout.cart.items) {
                    if (discount.product.doesMatch(item)) {
                        tracker.addItemToQueue(item);
                        // item.applicableDiscounts.push(tracker);
                    }
                }
                trackers.push(tracker);
            }
        }
        return trackers;
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], Discount.prototype, "id", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(DiscountRequirement) })
], Discount.prototype, "requirements", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], Discount.prototype, "applyMultipleTimes", void 0);
__decorate([
    field({ decoder: GeneralDiscount })
], Discount.prototype, "orderDiscount", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(ProductDiscountSettings) })
], Discount.prototype, "productDiscounts", void 0);
//# sourceMappingURL=Discount.js.map